import React from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthContext } from '../../auth/useAuthContext';
import { useGetStorefrontSettings } from '../../hooks/storefront/useGetStorefrontSettings';

/**
 * Specialised guard for /checkout and /checkout/success.
 *
 * Gate logic:
 *   1) authenticated user → always allowed.
 *   2) guest (X-Cart-Session) → allowed when
 *      `settings.checkout.allow_guest === true`.
 *   3) otherwise → redirect to /login.
 *
 * Kept separate from the generic ProtectedRoute so /profile, /orders,
 * /wishlist, etc. don't get accidentally guest-accessible.
 */
const CheckoutAccessRoute = ({ children }) => {
    const { isAuthenticated, isInitialized } = useAuthContext();
    const { allowGuestCheckout, isLoading: isSettingsLoading } = useGetStorefrontSettings();
    const { t } = useTranslation();

    // Wait for both the auth state and the settings to resolve so we
    // don't redirect to login flicker → checkout when allow_guest is on.
    if (!isInitialized || isSettingsLoading) {
        return <div>{t('common.loading')}</div>;
    }

    if (!isAuthenticated && !allowGuestCheckout) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

export default CheckoutAccessRoute;
